import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Lightbulb, Flag, Tag } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, ProgressBar, Ring, StatusDot } from "@/components/common";
import { LaunchNextStep } from "@/pages/launch/LaunchPlan";

const PlansLink = () => (
  <Link to="/plans" className="inline-flex items-center gap-2 text-sm font-semibold text-[#179B80]" data-testid="home-view-plans-link"><Tag className="h-4 w-4" />View plans</Link>
);

export const actionLink = (a) => {
  if (!a) return "/journey";
  if (["start_lesson", "continue_lesson", "exercise"].includes(a.type)) return `/lesson/${a.lesson_id}`;
  if (a.type === "done") return "/stage/GROW/output";
  return `/stage/${encodeURIComponent(a.stage)}`;
};

function NoBusiness({ name }) {
  return (
    <div className="space-y-5 stagger">
      <h1 className="text-3xl sm:text-4xl font-extrabold" data-testid="home-greeting">Hello {name}</h1>
      <div className="sb-card p-6 sm:p-8 bg-[#1E2B3C] text-white border-none relative overflow-hidden">
        <img src="/icon-512.png" alt="" className="absolute -right-10 -bottom-10 w-48 opacity-10 rotate-12" />
        <div className="sb-eyebrow text-[#1DB99A]">Step 1</div>
        <h2 className="text-2xl font-extrabold mt-2 max-w-md">Find a side business you can actually start</h2>
        <p className="text-white/70 text-sm mt-2 max-w-md">Explore 150 practical ideas adapted to Mauritius. Save the ones you like, compare them, then build one.</p>
        <Link to="/ideas" className="sb-btn-primary mt-6" data-testid="home-explore-ideas-btn"><Lightbulb className="h-4 w-4" /> Explore 150 ideas</Link>
      </div>
      <PlansLink />
    </div>
  );
}

export default function Home() {
  const { user, t, can } = useAuth();
  const [d, setD] = useState(null);
  const [lp, setLp] = useState(null);
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
    if (can("builder")) api.get("/launch-plan").then((r) => setLp(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <Loader />;
  if (!d.business) return <NoBusiness name={user.first_name} />;
  const cur = d.current_stage || {};
  const na = d.next_action;
  return (
    <div className="space-y-5 stagger" data-testid="home-dashboard">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold" data-testid="home-greeting">{t("Hello")} {user.first_name}</h1>
      </div>

      <section className="sb-card p-5 sm:p-6" data-testid="home-business-card">
        <div className="flex items-center gap-5">
          <Ring value={d.overall} size={76} stroke={8} />
          <div className="min-w-0 flex-1">
            <div className="sb-eyebrow">{t("MY BUSINESS")}</div>
            <div className="text-xl font-extrabold font-display truncate" data-testid="home-business-name">{d.business.name}</div>
            <div className="text-xs text-[#8C9BA5] truncate">{d.business.idea_name}</div>
          </div>
        </div>
        <div className="mt-5">
          <div className="flex justify-between text-xs font-semibold mb-1.5"><span className="sb-eyebrow">{t("OVERALL BUSINESS PROGRESS")}</span><span data-testid="home-overall-progress">{d.overall}%</span></div>
          <ProgressBar value={d.overall} tall />
          <div className="flex gap-4 mt-3 text-xs text-[#5C6B73]">
            <span>{d.lessons_done}/{d.lessons_total} lessons</span><span>{d.checklist_done}/{d.checklist_total} checklist</span><span>{d.exercises_done} exercises</span>
          </div>
        </div>
      </section>

      {lp ? <LaunchNextStep d={lp} /> : na && (
        <section className="rounded-2xl bg-[#1E2B3C] text-white p-5 sm:p-7 relative overflow-hidden" data-testid="home-next-step-card">
          <div className="absolute right-0 top-0 h-full w-1.5 bg-[#1DB99A]" />
          <div className="sb-eyebrow text-[#1DB99A]">{t("NEXT STEP")}</div>
          <h2 className="text-xl sm:text-2xl font-extrabold mt-2" data-testid="home-next-step-title">{na.title}</h2>
          <p className="text-sm text-white/70 mt-2 leading-relaxed max-w-xl">{na.description}</p>
          <Link to={actionLink(na)} className="sb-btn-primary mt-5 w-full sm:w-auto" data-testid="home-continue-btn">{t("Continue Course")} <ArrowRight className="h-4 w-4" /></Link>
        </section>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <section className="sb-card p-5" data-testid="home-current-stage">
          <div className="sb-eyebrow">{t("CURRENT STAGE")}</div>
          <div className="flex items-baseline justify-between mt-1">
            <div className="text-2xl font-extrabold font-display">{cur.name}</div>
            <div className="text-sm font-bold text-[#1DB99A]">{cur.pct}%</div>
          </div>
          <div className="text-sm text-[#5C6B73] mb-3">{cur.lessons_done} of {cur.lessons_total} {t("lessons completed")}</div>
          <ProgressBar value={cur.pct} />
          <div className="mt-5 sb-eyebrow">{t("THIS WEEK")}</div>
          <ul className="mt-3 space-y-2.5" data-testid="home-this-week">
            {d.this_week.map((i) => (
              <li key={i.key} className="flex items-center gap-3 text-sm"><StatusDot status={i.status} />
                <span className={i.status === "completed" ? "text-[#8C9BA5] line-through" : "font-medium"}>{i.label}</span></li>
            ))}
          </ul>
          <Link to={`/stage/${encodeURIComponent(cur.key || "IDEA")}`} className="sb-btn-ghost w-full mt-5 text-sm" data-testid="home-open-stage-btn">Open {cur.name} stage</Link>
        </section>

        <section className="sb-card p-5" data-testid="home-journey">
          <div className="sb-eyebrow mb-3">{t("YOUR JOURNEY")}</div>
          <ul className="space-y-1">
            {d.stages.map((s) => (
              <li key={s.key}>
                <Link to={`/stage/${encodeURIComponent(s.key)}`} className={`flex items-center gap-3 rounded-xl px-2 py-2.5 hover:bg-[#F5F0E6] transition-colors ${s.key === cur.key ? "bg-[#E8F8F4]" : ""}`} data-testid={`home-journey-stage-${s.key.replace(" ", "-")}`}>
                  <span className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold ${s.complete ? "bg-[#1DB99A] text-white" : s.key === cur.key ? "bg-[#1E2B3C] text-white" : "bg-[#F5F0E6] text-[#8C9BA5]"}`}>{s.complete ? <Check className="h-4 w-4" strokeWidth={3} /> : s.order}</span>
                  <span className="w-24 text-sm font-bold">{s.name}</span>
                  <ProgressBar value={s.pct} className="flex-1" />
                  <span className="w-10 text-right text-xs font-semibold text-[#5C6B73]">{s.pct}%</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="sb-card p-5" data-testid="home-milestones">
        <div className="flex items-center justify-between mb-4"><div className="sb-eyebrow">{t("Your Progress")} · {t("Milestones")}</div><Link to="/business#milestones" className="text-xs font-semibold text-[#179B80]">View all</Link></div>
        <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
          {d.milestones.map((m) => (
            <div key={m.key} className={`shrink-0 w-32 rounded-xl p-3 border ${m.achieved ? "border-[#1DB99A] bg-[#E8F8F4]" : "border-[#EADEC9] bg-[#FBF8F2]"}`} data-testid={`milestone-${m.key}`}>
              <Flag className={`h-4 w-4 ${m.achieved ? "text-[#1DB99A]" : "text-[#8C9BA5]"}`} />
              <div className={`text-xs font-bold mt-2 ${m.achieved ? "" : "text-[#8C9BA5]"}`}>{m.label}</div>
            </div>
          ))}
        </div>
      </section>
      <PlansLink />
    </div>
  );
}
